import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import API from "../../utils/api";
import { useAuth } from "../../App";

export default function AdminDashboard() {
  const { user } = useAuth();
  const expenseRole      = user?.app_role_overrides?.expense || user?.role;
  const isRestrictedAdmin = expenseRole === "admin" && user?.role !== "superadmin";

  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);

  // Cash bill approvals (finance step — manager already approved)
  const [cashBillApprovals, setCashBillApprovals] = useState([]);
  const [loadingCashBills, setLoadingCashBills] = useState(true);
  const [cashDeclineMode, setCashDeclineMode] = useState(null);
  const [cashDeclineNote, setCashDeclineNote] = useState("");
  const [cashProcessingId, setCashProcessingId] = useState(null);
  const [actionMsg, setActionMsg] = useState(null);
  const [oetMismatches, setOetMismatches] = useState(null); // cross-check summary

  useEffect(() => {
    API.get("/trips/team").then(r => { setTrips(r.data); setLoading(false); }).catch(() => setLoading(false));
    setLoadingCashBills(true);
    API.get("/expenses/cash-bill-approvals/pending")
      .then(r => setCashBillApprovals(r.data || []))
      .catch(() => setCashBillApprovals([]))
      .finally(() => setLoadingCashBills(false));
    API.get("/oet-exams/cross-check-summary")
      .then(r => setOetMismatches(r.data))
      .catch(() => {});
  }, []);

  const handleFinanceApprove = async (req) => {
    setCashProcessingId(req.id);
    try {
      await API.post(`/expenses/cash-bill-approvals/${req.id}/finance-approve`);
      setActionMsg({ type:"success", text:`✅ Cash bill of ₹${parseFloat(req.amount).toLocaleString("en-IN")} fully approved. Employee can now upload the bill.` });
      setCashBillApprovals(prev => prev.filter(r => r.id !== req.id));
    } catch (err) {
      setActionMsg({ type:"error", text:"❌ Failed to approve. Please try again." });
    } finally { setCashProcessingId(null); }
  };

  const handleFinanceDecline = async (req) => {
    setCashProcessingId(req.id);
    try {
      await API.post(`/expenses/cash-bill-approvals/${req.id}/finance-decline`, { notes: cashDeclineNote });
      setActionMsg({ type:"success", text:`Cash bill from ${req.employee_name} declined by Finance.` });
      setCashBillApprovals(prev => prev.filter(r => r.id !== req.id));
      setCashDeclineMode(null);
      setCashDeclineNote("");
    } catch (err) {
      setActionMsg({ type:"error", text:"❌ Failed to decline. Please try again." });
    } finally { setCashProcessingId(null); }
  };

  const viewBill = async (expenseId) => {
    try {
      const response = await API.get(`/expenses/${expenseId}/bill-file`, { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const win = window.open(url, "_blank", "noopener,noreferrer");
      if (!win) setActionMsg({ type:"error", text:"Allow popups to view bills." });
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      setActionMsg({ type:"error", text:"Could not open bill. It may not be attached yet." });
    }
  };

  const allTrips      = trips;
  const active        = trips.filter(t => t.status === "active");
  const submitted     = trips.filter(t => t.status === "submitted");
  const settled       = trips.filter(t => t.status === "settled");
  const totalAdvance  = trips.reduce((s,t) => s + parseFloat(t.advance_amount||0), 0);
  const totalSpent    = trips.reduce((s,t) => s + parseFloat(t.total_expenses||0), 0);
  const pendingSettle = submitted.reduce((s,t) => s + parseFloat(t.total_expenses||0), 0);

  const fmtStat = (n) => {
    const v = parseFloat(n || 0);
    if (v >= 10000000) return `₹${(v/10000000).toFixed(1)}Cr`;
    if (v >= 100000)   return `₹${(v/100000).toFixed(1)}L`;
    if (v >= 1000)     return `₹${(v/1000).toFixed(1)}K`;
    return `₹${v.toLocaleString("en-IN")}`;
  };

  const StatCard = ({ label, value, color, icon, border, sub }) => (
    <div className="stat-card" style={{ borderLeftColor: border || color }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
        <span style={{ fontSize:11, fontWeight:600, color:"#6b7280", textTransform:"uppercase", letterSpacing:"0.05em", lineHeight:1.3 }}>{label}</span>
        <span style={{ fontSize:20 }}>{icon}</span>
      </div>
      <div style={{ fontSize:24, fontWeight:800, color: color || "#1f2937", lineHeight:1.2, wordBreak:"break-word" }}>{value}</div>
      {sub && <div style={{ fontSize:11, color:"#9ca3af", marginTop:4 }}>{sub}</div>}
    </div>
  );

  return (
    <div>
      {/* Header Banner */}
      <div className="page-banner" style={{
        background:"linear-gradient(135deg,#E31E24 0%,#B71C1C 100%)",
        borderRadius:16, padding:"28px 32px", marginBottom:28, color:"white",
        display:"flex", alignItems:"center", justifyContent:"space-between"
      }}>
        <div>
          <div style={{ fontSize:22, fontWeight:800, marginBottom:6 }}>Admin Overview 🏢</div>
          <div style={{ fontSize:14, opacity:0.85 }}>Full visibility across all employees and trips.</div>
        </div>
        <div style={{ textAlign:"right" }}>
          <div style={{ fontSize:12, opacity:0.7, marginBottom:4 }}>Today</div>
          <div style={{ fontSize:15, fontWeight:600 }}>
            {new Date().toLocaleDateString("en-IN", { weekday:"long", day:"numeric", month:"long" })}
          </div>
        </div>
      </div>

      {/* ── OET Cross-Check Mismatch Alert ─────────────────────────── */}
      {!isRestrictedAdmin && oetMismatches && (oetMismatches.critical_count > 0 || oetMismatches.warning_count > 0) && (
        <div style={{
          background: oetMismatches.critical_count > 0 ? "#450a0a" : "#422006",
          border: `2px solid ${oetMismatches.critical_count > 0 ? "#991b1b" : "#92400e"}`,
          borderRadius: 14, padding: "16px 20px", marginBottom: 22,
        }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:10 }}>
            <div style={{ display:"flex", alignItems:"center", gap:10 }}>
              <span style={{ fontSize:22 }}>{oetMismatches.critical_count > 0 ? "🚨" : "⚠️"}</span>
              <div>
                <div style={{ fontSize:15, fontWeight:700, color:"#fca5a5" }}>
                  OET Staff/Billing Mismatch Detected
                </div>
                <div style={{ fontSize:13, color:"#fca5a5", opacity:0.8, marginTop:2 }}>
                  {oetMismatches.critical_count > 0 && `${oetMismatches.critical_count} critical mismatch${oetMismatches.critical_count > 1 ? "es" : ""} — declared counts don't match bills received. `}
                  {oetMismatches.warning_count  > 0 && `${oetMismatches.warning_count} warning${oetMismatches.warning_count > 1 ? "s" : ""} — minor variance, review recommended.`}
                </div>
                <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginTop:8 }}>
                  {oetMismatches.sessions?.filter(s => s.severity !== "ok").map(s => (
                    <span key={s.session_id} style={{
                      fontSize:11, padding:"3px 10px", borderRadius:6, fontWeight:600,
                      background: s.severity === "critical" ? "#7f1d1d" : "#78350f",
                      color: s.severity === "critical" ? "#fca5a5" : "#fde68a",
                      border: `1px solid ${s.severity === "critical" ? "#ef4444" : "#f59e0b"}`,
                    }}>
                      {s.severity === "critical" ? "🚨" : "⚠️"} {s.center_name} — {new Date(s.exam_date+"T00:00:00").toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"numeric"})}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <Link to="/admin/oet-exams" style={{
              background:"#dc2626", color:"#fff", borderRadius:10,
              padding:"10px 18px", fontWeight:700, fontSize:13,
              textDecoration:"none", whiteSpace:"nowrap",
            }}>
              Review in OET Costs →
            </Link>
          </div>
        </div>
      )}

      {/* Stat Cards */}
      <div className="stats-grid" style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))", gap:12, marginBottom:28 }}>
        <StatCard label="Total Trips"      value={allTrips.length}  icon="✈️"  border="#6366f1" color="#4f46e5" />
        <StatCard label="Active"           value={active.length}    icon="🟢"  border="#10b981" color="#059669" />
        <StatCard label="Pending Review"   value={submitted.length} icon="🕐"  border="#f59e0b" color="#d97706" sub={submitted.length > 0 ? "Need settlement" : "All clear"} />
        <StatCard label="Settled"          value={settled.length}   icon="✅"  border="#6366f1" color="#4f46e5" />
        {!isRestrictedAdmin && <StatCard label="Total Advances"   value={fmtStat(totalAdvance)}  icon="💰" border="#d97706" color="#d97706" />}
        <StatCard label="Total Spent"      value={fmtStat(totalSpent)}    icon="💸" border="#E31E24" color="#E31E24" />
        <StatCard label="Pending Settle"   value={fmtStat(pendingSettle)} icon="⚠️" border="#DC2626" color="#DC2626" sub="Submitted trips" />
      </div>

      {/* Quick Links */}
      {!isRestrictedAdmin && (
        <div style={{ display:"flex", gap:12, marginBottom:28, flexWrap:"wrap" }}>
          <Link to="/admin/advances" className="btn btn-primary" style={{ fontSize:14 }}>
            💰 Issue Advance / Settle
          </Link>
        </div>
      )}

      {/* Action message */}
      {actionMsg && (
        <div style={{
          marginBottom:20, padding:"14px 20px", borderRadius:12, fontWeight:600, fontSize:14,
          background: actionMsg.type === "success" ? "#D1FAE5" : "#FEE2E2",
          color: actionMsg.type === "success" ? "#065F46" : "#991B1B",
          border: `1px solid ${actionMsg.type === "success" ? "#6EE7B7" : "#FECACA"}`,
          display:"flex", alignItems:"center", justifyContent:"space-between"
        }}>
          <span>{actionMsg.text}</span>
          <button onClick={() => setActionMsg(null)} style={{ background:"none", border:"none", cursor:"pointer", fontSize:18, color:"inherit", opacity:0.6 }}>×</button>
        </div>
      )}

      {/* ── Cash Voucher Finance Approvals ── */}
      {(loadingCashBills || cashBillApprovals.length > 0) && (
        <div className="card" style={{ marginBottom:24, border:"2px solid #FCA5A5", background:"#FFF5F5" }}>
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom: cashBillApprovals.length > 0 ? 16 : 0 }}>
            <span style={{ fontSize:22 }}>🧾</span>
            <div style={{ flex:1 }}>
              <h3 style={{ fontSize:16, fontWeight:700, color:"#991B1B", margin:0 }}>
                Cash Voucher — Finance Approval
                {cashBillApprovals.length > 0 && (
                  <span style={{ marginLeft:8, background:"#DC2626", color:"white", borderRadius:20, padding:"2px 10px", fontSize:13 }}>
                    {cashBillApprovals.length}
                  </span>
                )}
              </h3>
              <p style={{ fontSize:12, color:"#b91c1c", margin:0, marginTop:2 }}>
                Manager has approved — awaiting your finance confirmation
              </p>
            </div>
          </div>

          {loadingCashBills ? (
            <div style={{ textAlign:"center", padding:"20px 0", color:"#9ca3af" }}>Loading...</div>
          ) : cashBillApprovals.length === 0 ? (
            <div style={{ textAlign:"center", padding:"16px 0", color:"#6b7280", fontSize:14 }}>
              ✅ No pending finance approvals
            </div>
          ) : (
            <div style={{ display:"grid", gap:14 }}>
              {cashBillApprovals.map(req => {
                const isDeclineOpen = cashDeclineMode === req.id;
                const isProcessing = cashProcessingId === req.id;
                return (
                  <div key={req.id} style={{
                    background:"white", borderRadius:12, padding:"18px 20px",
                    border:"1px solid #FECACA", boxShadow:"0 1px 4px rgba(0,0,0,0.06)"
                  }}>
                    <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", flexWrap:"wrap", gap:12, marginBottom:12 }}>
                      <div>
                        <div style={{ fontSize:15, fontWeight:800, color:"#1f2937" }}>{req.employee_name}</div>
                        <div style={{ fontSize:12, color:"#9ca3af", marginTop:2 }}>{req.employee_email}</div>
                        <div style={{ fontSize:12, color:"#6b7280", marginTop:4 }}>
                          📅 {new Date(req.created_at).toLocaleString("en-IN", { day:"numeric", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit" })}
                        </div>
                        <div style={{ marginTop:6, fontSize:12, padding:"3px 8px", background:"#D1FAE5", color:"#065F46", borderRadius:6, display:"inline-block", fontWeight:600 }}>
                          ✅ Manager Approved
                        </div>
                      </div>
                      <div style={{ textAlign:"right" }}>
                        <div style={{ fontSize:11, color:"#9ca3af", fontWeight:600, textTransform:"uppercase" }}>Cash Voucher Amount</div>
                        <div style={{ fontSize:22, fontWeight:800, color:"#DC2626" }}>
                          ₹{parseFloat(req.amount).toLocaleString("en-IN")}
                        </div>
                        {/* The cap is on the day as a whole, so show what the
                            employee's cash vouchers for that date come to. */}
                        {req.cash_voucher_day_total != null && (
                          <div style={{ fontSize:12, color:"#6b7280", marginTop:2 }}>
                            ₹{parseFloat(req.cash_voucher_day_total).toLocaleString("en-IN")} total for {new Date(req.expense_date).toLocaleDateString("en-IN")}
                          </div>
                        )}
                      </div>
                    </div>

                    <div style={{ background:"#F9FAFB", borderRadius:8, padding:"10px 14px", marginBottom:14, display:"flex", flexWrap:"wrap", gap:16 }}>
                      <div>
                        <div style={{ fontSize:11, color:"#9ca3af", fontWeight:600, textTransform:"uppercase" }}>Trip</div>
                        <div style={{ fontSize:14, fontWeight:700, color:"#1f2937", marginTop:1 }}>{req.trip_title}</div>
                      </div>
                      <div>
                        <div style={{ fontSize:11, color:"#9ca3af", fontWeight:600, textTransform:"uppercase" }}>Category</div>
                        <div style={{ fontSize:14, color:"#374151", marginTop:1 }}>{req.category}</div>
                      </div>
                      <div>
                        <div style={{ fontSize:11, color:"#9ca3af", fontWeight:600, textTransform:"uppercase" }}>Description</div>
                        <div style={{ fontSize:13, color:"#374151", marginTop:1 }}>{req.description}</div>
                      </div>
                      <div>
                        <div style={{ fontSize:11, color:"#9ca3af", fontWeight:600, textTransform:"uppercase" }}>Expense Date</div>
                        <div style={{ fontSize:13, color:"#374151", marginTop:1 }}>
                          {new Date(req.expense_date).toLocaleDateString("en-IN")}
                        </div>
                      </div>
                    </div>

                    {isDeclineOpen && (
                      <div style={{ marginBottom:12 }}>
                        <label style={{ fontSize:13, fontWeight:600, color:"#374151", display:"block", marginBottom:6 }}>
                          Reason for declining <span style={{ color:"#9ca3af", fontWeight:400 }}>(optional)</span>
                        </label>
                        <textarea
                          value={cashDeclineNote}
                          onChange={e => setCashDeclineNote(e.target.value)}
                          placeholder="e.g. Please provide a proper printed receipt."
                          rows={2}
                          style={{ width:"100%", padding:"10px 12px", borderRadius:8, border:"1px solid #E5E7EB", fontSize:13, resize:"vertical", fontFamily:"inherit", boxSizing:"border-box" }}
                        />
                      </div>
                    )}

                    <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
                      {Number(req.has_bill) ? (
                        <button type="button" onClick={() => viewBill(req.expense_id)}
                          style={{ background:"white", color:"#0f766e", border:"2px solid #0d9488", borderRadius:8, padding:"10px 22px", fontSize:14, fontWeight:700, cursor:"pointer" }}>
                          📎 View Bill
                        </button>
                      ) : (
                        <span style={{ fontSize:12, color:"#9ca3af", alignSelf:"center" }}>No bill attached</span>
                      )}
                      {!isDeclineOpen ? (
                        <>
                          <button
                            disabled={isProcessing}
                            onClick={() => handleFinanceApprove(req)}
                            style={{
                              background: isProcessing ? "#9ca3af" : "#059669",
                              color:"white", border:"none", borderRadius:8,
                              padding:"10px 22px", fontSize:14, fontWeight:700,
                              cursor: isProcessing ? "not-allowed" : "pointer"
                            }}>
                            {isProcessing ? "Processing..." : "✅ Finance Approve"}
                          </button>
                          <button
                            disabled={isProcessing}
                            onClick={() => { setCashDeclineMode(req.id); setCashDeclineNote(""); }}
                            style={{
                              background:"white", color:"#DC2626", border:"2px solid #DC2626",
                              borderRadius:8, padding:"10px 22px", fontSize:14, fontWeight:700,
                              cursor: isProcessing ? "not-allowed" : "pointer"
                            }}>
                            ❌ Decline
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            disabled={isProcessing}
                            onClick={() => handleFinanceDecline(req)}
                            style={{
                              background: isProcessing ? "#9ca3af" : "#DC2626",
                              color:"white", border:"none", borderRadius:8,
                              padding:"10px 22px", fontSize:14, fontWeight:700,
                              cursor: isProcessing ? "not-allowed" : "pointer"
                            }}>
                            {isProcessing ? "Processing..." : "Confirm Decline"}
                          </button>
                          <button
                            disabled={isProcessing}
                            onClick={() => { setCashDeclineMode(null); setCashDeclineNote(""); }}
                            style={{
                              background:"white", color:"#6b7280", border:"1px solid #D1D5DB",
                              borderRadius:8, padding:"10px 18px", fontSize:14, fontWeight:600, cursor:"pointer"
                            }}>
                            Cancel
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Submitted Trips needing settlement */}
      {submitted.length > 0 && (
        <div className="card" style={{ marginBottom:24, border:"2px solid #FEE2E2" }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
            <div style={{ display:"flex", alignItems:"center", gap:10 }}>
              <span style={{ fontSize:20 }}>⚠️</span>
              <h3 style={{ fontSize:16, fontWeight:700, color:"#991b1b" }}>Trips Awaiting Settlement ({submitted.length})</h3>
            </div>
            {!isRestrictedAdmin && <Link to="/admin/advances" className="btn btn-primary btn-sm">Go to Advances →</Link>}
          </div>
          <div style={{ display:"grid", gap:10 }}>
            {submitted.map(trip => {
              const bal = parseFloat(trip.advance_amount||0) - parseFloat(trip.total_expenses||0);
              return (
                <div key={trip.id} style={{ background:"#FFF5F5", borderRadius:10, padding:"14px 18px", display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:10 }}>
                  <div>
                    <div style={{ fontWeight:700, color:"#1f2937" }}>{trip.title}</div>
                    <div style={{ fontSize:13, color:"#6b7280", marginTop:2 }}>
                      👤 {trip.employee_name || "—"} &nbsp;•&nbsp; 📍 {trip.destination || "—"}
                    </div>
                  </div>
                  <div style={{ display:"flex", gap:20, alignItems:"center" }}>
                    <div style={{ textAlign:"center" }}>
                      <div style={{ fontSize:11, color:"#9ca3af", fontWeight:600 }}>ADVANCE</div>
                      <div style={{ fontSize:15, fontWeight:800, color:"#d97706" }}>₹{parseFloat(trip.advance_amount||0).toLocaleString("en-IN")}</div>
                    </div>
                    <div style={{ textAlign:"center" }}>
                      <div style={{ fontSize:11, color:"#9ca3af", fontWeight:600 }}>SPENT</div>
                      <div style={{ fontSize:15, fontWeight:800, color:"#E31E24" }}>₹{parseFloat(trip.total_expenses||0).toLocaleString("en-IN")}</div>
                    </div>
                    <div style={{ textAlign:"center" }}>
                      <div style={{ fontSize:11, color:"#9ca3af", fontWeight:600 }}>
                        {bal>=0 ? "REFUND DUE" : "PAY MORE"}
                      </div>
                      <div style={{ fontSize:15, fontWeight:800, color:bal>=0?"#059669":"#DC2626" }}>
                        ₹{Math.abs(bal).toLocaleString("en-IN")}
                      </div>
                    </div>
                    {!isRestrictedAdmin && <Link to="/admin/advances" className="btn btn-primary btn-sm">Settle →</Link>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* All Trips Table */}
      <div className="card">
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:20 }}>
          <div>
            <h3 style={{ fontSize:17, fontWeight:700, color:"#1f2937" }}>All Employee Trips</h3>
            <p style={{ fontSize:13, color:"#9ca3af", marginTop:2 }}>Full company-wide expense history</p>
          </div>
        </div>
        {loading ? (
          <div style={{ textAlign:"center", padding:40, color:"#9ca3af" }}>Loading...</div>
        ) : trips.length === 0 ? (
          <div className="empty-state">
            <div style={{ fontSize:48 }}>✈️</div>
            <h3>No trips yet</h3>
            <p>Trips will appear here once employees create them</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table>
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Trip</th>
                  <th>Destination</th>
                  <th>Dates</th>
                  <th>Advance</th>
                  <th>Spent</th>
                  <th>Balance</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {trips.map(trip => {
                  const balance = parseFloat(trip.advance_amount||0) - parseFloat(trip.total_expenses||0);
                  return (
                    <tr key={trip.id}>
                      <td>
                        <div style={{ fontWeight:600, color:"#1f2937" }}>{trip.employee_name || "—"}</div>
                        <div style={{ fontSize:12, color:"#9ca3af" }}>{trip.employee_email}</div>
                      </td>
                      <td>
                        <div style={{ fontWeight:600, color:"#1f2937" }}>{trip.title}</div>
                        <div style={{ fontSize:12, color:"#9ca3af" }}>{trip.purpose}</div>
                      </td>
                      <td style={{ color:"#6b7280" }}>{trip.destination || "—"}</td>
                      <td style={{ fontSize:13, color:"#6b7280" }}>
                        {new Date(trip.start_date).toLocaleDateString("en-IN")}
                        {trip.end_date && <><br/>{new Date(trip.end_date).toLocaleDateString("en-IN")}</>}
                      </td>
                      <td style={{ fontWeight:600 }}>₹{parseFloat(trip.advance_amount||0).toLocaleString("en-IN")}</td>
                      <td style={{ fontWeight:600, color:"#E31E24" }}>₹{parseFloat(trip.total_expenses||0).toLocaleString("en-IN")}</td>
                      <td style={{ fontWeight:700, color:balance>=0?"#059669":"#DC2626" }}>
                        {balance>=0?"+":""}₹{Math.abs(balance).toLocaleString("en-IN")}
                      </td>
                      <td><span className={`badge badge-${trip.status}`}>{trip.status}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
